import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Card,
  CardContent,
  Divider,
  IconButton,
  Chip,
  alpha,
  useTheme,
  Alert,
  InputAdornment,
  Avatar,
} from '@mui/material';
import {
  Save as SaveIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Settings as SettingsIcon,
  AccountBalance as AccountBalanceIcon,
  LocationOn as LocationIcon,
  CalendarToday as CalendarIcon,
  AttachMoney as AttachMoneyIcon,
  Person as PersonIcon,
  PersonPin as PersonPinIcon,
} from '@mui/icons-material';
import { useMaterialUIController, useGlobalMDAlert } from 'context';
import { useApi } from 'lib/api';

const ConfiguracionClientes = ({ clienteId, onGuardar }) => {
  const [controller] = useMaterialUIController();
  const { nombreNegocio } = controller;
  const { showAlert } = useGlobalMDAlert();
  const { post } = useApi();
  const showSuccess = (message) =>
    showAlert({ message, color: 'success', autoHideDuration: 3000 });
  const showError = (message) =>
    showAlert({ message, color: 'error', autoHideDuration: 4000 });
  const selectedCompany = nombreNegocio;
  const theme = useTheme();

  // Estados del formulario
  const [clienteSeleccionado, setClienteSeleccionado] = useState(null);
  const [limiteCredito, setLimiteCredito] = useState(0);
  const [limiteCompra, setLimiteCompra] = useState(0);
  const [areaGeografica, setAreaGeografica] = useState('');
  const [diasCredito, setDiasCredito] = useState(30);
  const [montoMaximoFacturacion, setMontoMaximoFacturacion] = useState(0);
  const [vendedor, setVendedor] = useState('');
  
  // Estados auxiliares
  const [vendedores, setVendedores] = useState([]);
  const [areasDisponibles, setAreasDisponibles] = useState([]);
  const [listasPrecios, setListasPrecios] = useState([]);
  const [listaPrecioCliente, setListaPrecioCliente] = useState('');
  const [cargando, setCargando] = useState(false);

  const opcionesDiasCredito = [30, 60, 90, 120, 150, 180];

  useEffect(() => {
    cargarVendedores();
    cargarAreas();
    cargarListasPrecios();
  }, [selectedCompany]);

  useEffect(() => {
    if (clienteId) {
      cargarDatosCliente(clienteId);
    }
  }, [clienteId]);

  // Efecto para cargar el vendedor cuando se cargan los datos del cliente y los vendedores
  useEffect(() => {
    if (clienteSeleccionado && vendedores.length > 0) {
      const vendedorId = clienteSeleccionado.vendedor ? String(clienteSeleccionado.vendedor) : '';
      const vendedorEncontrado = vendedores.find(v => v.texto.startsWith(vendedorId + '-'));
      if (vendedorEncontrado) {
        setVendedor(vendedorEncontrado.texto);
      } else {
        setVendedor('');
      }
    }
  }, [clienteSeleccionado, vendedores]);

  const cargarDatosCliente = async (id) => {
    setCargando(true);
    try {
      const { ok, data } = await post('/api/clientesbackend_obtenerporid', {
        id_empresa: selectedCompany,
        id: id,
      });

      if (ok) {
        const cliente = data.cliente;
        setClienteSeleccionado(cliente);
        
        // Cargar valores de configuración
        setLimiteCredito(cliente.limcredito || 0);
        setLimiteCompra(cliente.limcompra || 0);
        setAreaGeografica(cliente.area ? String(cliente.area) : '');
        setListaPrecioCliente(cliente.lista_precio_id ? String(cliente.lista_precio_id) : '');
        // Nota: diasCredito y montoMaximoFacturacion pueden necesitar campos adicionales en la BD
        // Por ahora los dejamos en valores por defecto
        // El vendedor se cargará en un useEffect separado cuando vendedores esté disponible
      } else {
        showError('Error al cargar los datos del cliente');
      }
    } catch (error) {
      showError('Error de conexión al cargar cliente');
    } finally {
      setCargando(false);
    }
  };

  const cargarVendedores = async () => {
    try {
      const { ok, data } = await post('/api/clientesbackend_obtenervendedores', {
        id_empresa: selectedCompany,
      });

      if (ok) {
        setVendedores(data.vendedores || []);
      } else {
        showError(data.Error || 'Error al cargar vendedores');
      }
    } catch (error) {
      showError('Error de conexión al cargar vendedores');
    }
  };

  const cargarAreas = async () => {
    try {
      const { ok, data } = await post('/api/clientesbackend_listarareas', {
        id_empresa: selectedCompany,
      });
      if (ok) {
        setAreasDisponibles(data.zonas || []);
      }
    } catch (error) {
      console.error('Error al cargar áreas:', error);
    }
  };

  const cargarListasPrecios = async () => {
    try {
      const { ok, data } = await post('/api/productosbackend_listarlistasprecios', {
        id_empresa: selectedCompany,
      });
      if (ok) {
        setListasPrecios(data.listas || []);
      }
    } catch (error) {
      console.error('Error al cargar listas de precios:', error);
    }
  };

  const validarFormulario = () => {
    if (!clienteSeleccionado) {
      showError('Debe seleccionar un cliente primero');
      return false;
    }
    if (clienteSeleccionado.tiposuplidor === 'Cliente' && !areaGeografica.trim()) {
      showError('El área geográfica es requerida para clientes');
      return false;
    }
    return true;
  };

  const handleGuardarConfiguracion = async () => {
    if (!validarFormulario()) {
      return;
    }

    try {
      const { ok, data } = await post('/api/clientesbackend_modificar', {
        id_empresa: selectedCompany,
        nombre: clienteSeleccionado.descrip,
        direccion: clienteSeleccionado.direccion,
        ciudad: clienteSeleccionado.ciudad,
        telefono: clienteSeleccionado.telefono,
        fax: clienteSeleccionado.fax,
        email: clienteSeleccionado.email,
        vendedor: vendedor,
        rnc: clienteSeleccionado.rnc,
        limcredito: limiteCredito,
        limcompra: limiteCompra,
        desc1: clienteSeleccionado.desc1 || 0,
        desc2: clienteSeleccionado.desc2 || 0,
        desc3: clienteSeleccionado.desc3 || 0,
        tipo: clienteSeleccionado.tiposuplidor,
        area: clienteSeleccionado.tiposuplidor === 'Cliente' ? areaGeografica : null,
        lista_precio_id:
          clienteSeleccionado.tiposuplidor === 'Cliente' && listaPrecioCliente
            ? parseInt(listaPrecioCliente, 10)
            : null,
      });

      if (ok) {
        showSuccess('Configuración guardada exitosamente');
        if (onGuardar) {
          limpiarFormulario();
          onGuardar();
        }
      } else {
        showError(data.Error || 'Error al guardar la configuración');
      }
    } catch (error) {
      showError('Error de conexión al guardar');
    }
  };

  const limpiarFormulario = () => {
    setLimiteCredito(0);
    setLimiteCompra(0);
    setAreaGeografica('');
    setListaPrecioCliente('');
    setDiasCredito(30);
    setMontoMaximoFacturacion(0);
    setVendedor('');
    setClienteSeleccionado(null);
  };

  if (!clienteId) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info" sx={{ borderRadius: 2 }}>
          <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
            Seleccione un cliente
          </Typography>
          <Typography variant="body2">
            Para configurar los parámetros de un cliente, primero debe seleccionarlo desde el listado o buscarlo.
          </Typography>
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      {/* Header */}
      <Box
        sx={{
          mb: 3,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar
            sx={{
              width: 48,
              height: 48,
              bgcolor: theme.palette.secondary.main,
            }}
          >
            <SettingsIcon />
          </Avatar>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, color: 'text.primary' }}>
              Configuración de Cliente
            </Typography>
            {clienteSeleccionado && (
              <Typography variant="body2" color="text.secondary">
                {clienteSeleccionado.descrip} - {clienteSeleccionado.rnc}
              </Typography>
            )}
          </Box>
        </Box>
        <Chip
          icon={<PersonIcon />}
          label={clienteSeleccionado?.tiposuplidor || 'Cliente'}
          color={clienteSeleccionado?.tiposuplidor === 'Cliente' ? 'primary' : 'secondary'}
          sx={{ fontWeight: 600 }}
        />
      </Box>

      {cargando ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography>Cargando configuración...</Typography>
        </Box>
      ) : (
        <Grid container spacing={3}>
          {/* Límites de Crédito y Compra */}
          <Grid item xs={12} md={6}>
            <Card
              sx={{
                borderRadius: 3,
                boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                height: '100%',
              }}
            >
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                  <AccountBalanceIcon color="primary" />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Límites Financieros
                  </Typography>
                </Box>
                <Divider sx={{ mb: 3 }} />

                {clienteSeleccionado?.tiposuplidor === 'Cliente' ? (
                  <TextField
                    fullWidth
                    label="Límite de Crédito"
                    value={limiteCredito}
                    onChange={(e) => setLimiteCredito(parseFloat(e.target.value) || 0)}
                    type="number"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <AttachMoneyIcon sx={{ color: 'text.secondary' }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      mb: 3,
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                      },
                    }}
                    helperText="Monto máximo de crédito que se puede otorgar al cliente"
                  />
                ) : (
                  <TextField
                    fullWidth
                    label="Límite de Compra"
                    value={limiteCompra}
                    onChange={(e) => setLimiteCompra(parseFloat(e.target.value) || 0)}
                    type="number"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <AttachMoneyIcon sx={{ color: 'text.secondary' }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      mb: 3,
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                      },
                    }}
                    helperText="Monto máximo de compra permitido para el suplidor"
                  />
                )}

                <TextField
                  fullWidth
                  label="Monto Máximo para Facturación"
                  value={montoMaximoFacturacion}
                  onChange={(e) => setMontoMaximoFacturacion(parseFloat(e.target.value) || 0)}
                  type="number"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <AttachMoneyIcon sx={{ color: 'text.secondary' }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    },
                  }}
                  helperText="Monto máximo permitido en una factura individual"
                />
              </CardContent>
            </Card>
          </Grid>

          {/* Área Geográfica, Vendedor y Días de Crédito */}
          <Grid item xs={12} md={6}>
            <Card
              sx={{
                borderRadius: 3,
                boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                height: '100%',
              }}
            >
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                  <LocationIcon color="primary" />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Ubicación y Términos
                  </Typography>
                </Box>
                <Divider sx={{ mb: 3 }} />

                {/* Vendedor */}
                <Box sx={{ mb: 3 }}>
                  <FormControl fullWidth>
                    <InputLabel>Vendedor</InputLabel>
                    <Select
                      value={vendedor}
                      onChange={(e) => setVendedor(e.target.value)}
                      label="Vendedor"
                      sx={{
                        borderRadius: 2,
                      }}
                      startAdornment={
                        <InputAdornment position="start" sx={{ ml: 1 }}>
                          <PersonPinIcon sx={{ color: 'text.secondary' }} />
                        </InputAdornment>
                      }
                    >
                      {vendedores.map((vend) => (
                        <MenuItem key={vend.id} value={vend.texto}>
                          {vend.texto}
                        </MenuItem>
                      ))}
                    </Select>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                      Vendedor asignado al cliente/suplidor
                    </Typography>
                  </FormControl>
                </Box>

                {clienteSeleccionado?.tiposuplidor === 'Cliente' && (
                  <Box sx={{ mb: 3 }}>
                    <FormControl fullWidth>
                      <InputLabel>Área Geográfica</InputLabel>
                      <Select
                        value={areaGeografica}
                        onChange={(e) => setAreaGeografica(e.target.value)}
                        label="Área Geográfica"
                        sx={{ borderRadius: 2 }}
                        startAdornment={
                          <InputAdornment position="start" sx={{ ml: 1 }}>
                            <LocationIcon sx={{ color: 'text.secondary' }} />
                          </InputAdornment>
                        }
                      >
                        <MenuItem value="">
                          <em>Sin asignar</em>
                        </MenuItem>
                        {areasDisponibles.map((a) => (
                          <MenuItem key={a.id} value={String(a.id)}>
                            {a.id} – {a.zona || a.descrip || '-'}
                          </MenuItem>
                        ))}
                      </Select>
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                        Zona de ventas del cliente (gestión en Facturación → Configuración Fiscal → Área Geográfica Ventas)
                      </Typography>
                    </FormControl>
                  </Box>
                )}

                {clienteSeleccionado?.tiposuplidor === 'Cliente' && (
                  <Box sx={{ mb: 3 }}>
                    <FormControl fullWidth>
                      <InputLabel>Lista de Precios</InputLabel>
                      <Select
                        value={listaPrecioCliente}
                        onChange={(e) => setListaPrecioCliente(e.target.value)}
                        label="Lista de Precios"
                        sx={{ borderRadius: 2 }}
                        startAdornment={
                          <InputAdornment position="start" sx={{ ml: 1 }}>
                            <AttachMoneyIcon sx={{ color: 'text.secondary' }} />
                          </InputAdornment>
                        }
                      >
                        <MenuItem value="">
                          <em>Sin lista asignada (precio normal)</em>
                        </MenuItem>
                        {listasPrecios.map((l) => (
                          <MenuItem key={l.id} value={String(l.id)}>
                            {l.codigo} – {l.descripcion || '-'}
                          </MenuItem>
                        ))}
                      </Select>
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                        Lista de precios que se aplicará por defecto al cliente en facturación
                      </Typography>
                    </FormControl>
                  </Box>
                )}

                <FormControl fullWidth sx={{ mb: 3 }}>
                  <InputLabel>Días de Crédito</InputLabel>
                  <Select
                    value={diasCredito}
                    onChange={(e) => setDiasCredito(e.target.value)}
                    label="Días de Crédito"
                    sx={{
                      borderRadius: 2,
                    }}
                    startAdornment={
                      <InputAdornment position="start" sx={{ ml: 1 }}>
                        <CalendarIcon sx={{ color: 'text.secondary' }} />
                      </InputAdornment>
                    }
                  >
                    {opcionesDiasCredito.map((dias) => (
                      <MenuItem key={dias} value={dias}>
                        {dias} días
                      </MenuItem>
                    ))}
                    <MenuItem value={999}>
                      Más de 180 días
                    </MenuItem>
                  </Select>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    Plazo de crédito otorgado al cliente para el pago de facturas
                  </Typography>
                </FormControl>
              </CardContent>
            </Card>
          </Grid>

          {/* Resumen de Configuración */}
          <Grid item xs={12}>
            <Card
              sx={{
                borderRadius: 3,
                boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                bgcolor: alpha(theme.palette.primary.main, 0.05),
              }}
            >
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                  Resumen de Configuración
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={3}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        {clienteSeleccionado?.tiposuplidor === 'Cliente' ? 'Límite Crédito' : 'Límite Compra'}
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        ${(clienteSeleccionado?.tiposuplidor === 'Cliente' ? limiteCredito : limiteCompra).toLocaleString('es-DO', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </Typography>
                    </Box>
                  </Grid>
                  {clienteSeleccionado?.tiposuplidor === 'Cliente' && (
                    <Grid item xs={12} sm={6} md={3}>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Área Geográfica
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>
                          {areaGeografica
                            ? (areasDisponibles.find((a) => String(a.id) === areaGeografica)?.zona || areasDisponibles.find((a) => String(a.id) === areaGeografica)?.descrip || `ID ${areaGeografica}`)
                            : 'No asignada'}
                        </Typography>
                      </Box>
                    </Grid>
                  )}
                  {clienteSeleccionado?.tiposuplidor === 'Cliente' && (
                    <Grid item xs={12} sm={6} md={3}>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Lista de Precios
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>
                          {listaPrecioCliente
                            ? (() => {
                                const lp = listasPrecios.find((l) => String(l.id) === String(listaPrecioCliente));
                                return lp ? `${lp.codigo} – ${lp.descripcion || ''}` : `ID ${listaPrecioCliente}`;
                              })()
                            : 'Precio normal'}
                        </Typography>
                      </Box>
                    </Grid>
                  )}
                  <Grid item xs={12} sm={6} md={3}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Días de Crédito
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        {diasCredito === 999 ? 'Más de 180' : diasCredito} días
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Máximo Facturación
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        ${montoMaximoFacturacion.toLocaleString('es-DO', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Botones de Acción */}
          <Grid item xs={12}>
            <Box
              sx={{
                display: 'flex',
                gap: 2,
                justifyContent: 'flex-end',
                flexWrap: 'wrap',
              }}
            >
              <Button
                variant="outlined"
                startIcon={<ClearIcon />}
                onClick={limpiarFormulario}
                sx={{
                  borderRadius: 2,
                  px: 3,
                  textTransform: 'none',
                  fontWeight: 600,
                }}
              >
                Limpiar
              </Button>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleGuardarConfiguracion}
                sx={{
                  borderRadius: 2,
                  px: 4,
                  textTransform: 'none',
                  fontWeight: 600,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                  boxShadow: '0 4px 15px rgba(0,0,0,0.2)',
                  '&:hover': {
                    boxShadow: '0 6px 20px rgba(0,0,0,0.3)',
                  },
                }}
              >
                Guardar Configuración
              </Button>
            </Box>
          </Grid>
        </Grid>
      )}

    </Box>
  );
};

export default ConfiguracionClientes;
